/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { FunctionComponent, SVGProps, useCallback, useEffect, useRef, useState } from 'react';
import Style from './appDropDownWithSuggestion.module.scss';
import { AppTextInput } from 'uikit/inputs';
import { Callout, DirectionalHint } from '@fluentui/react';
import { DropdownOption } from './appDropdownInput';
import { TypographyStyles } from 'styles';
import { ReactComponent as Cross } from 'src/assets/img/common/cross_16.svg';
import { ReactComponent as Chevron } from 'src/assets/img/common/blueChevron.svg';
import clsx from 'clsx';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { DropdownOptionComponent } from './dropdownOption.component';
import { useId } from '@fluentui/react-hooks';

export type AppDropDownWithSuggestionProps<T extends DropdownOption> = {
  options: T[];
  disabled?: boolean;
  suggestion?: boolean;
  invalid?: boolean;
  placeholder?: string;
  CaretIcon?: FunctionComponent<SVGProps<any>>;
  caretClassName?: string;
  isRequired?: boolean;
  className?: string;
  inputClassName?: string;
  hideCaret?: boolean;
} & (
  | {
      isMultiple: true;
      value?: T[];
      onChange?: (newValue: T[] | undefined) => void;
      emptyOptionLabel?: never;
    }
  | {
      isMultiple?: false | undefined;
      value?: T;
      onChange?: (newValue: T | undefined) => void;
      emptyOptionLabel?: string;
    }
);

export const AppDropDownWithSuggestionInput = <T extends DropdownOption>({
  options,
  value,
  onChange,
  isMultiple,
  disabled,
  suggestion,
  placeholder,
  invalid,
  CaretIcon,
  emptyOptionLabel,
  isRequired,
  className,
  caretClassName,
  hideCaret,
  inputClassName,
}: AppDropDownWithSuggestionProps<T>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLElement | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [input, setInput] = useState<string>(isMultiple ? '' : value?.text ?? '');
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const [optionRefsArray] = useState<(HTMLButtonElement | null)[]>(() =>
    Array.from({ length: options.length }, () => null),
  );

  // This effect sets the input value if the component already mounted
  useEffect(() => {
    setInput(isMultiple ? '' : value?.text ?? '');
  }, [isMultiple, value]);

  // This id is used to find dropdown options in UI tests
  const calloutId = useId();

  const onOptionClick = useCallback(
    (option: T) => () => {
      if (isMultiple) {
        if (suggestion && input.length) {
          setInput('');
        }

        inputRef.current?.focus();
        const added = value?.some((o) => o.key === option.key);
        return onChange?.(added ? value?.filter((o) => o.key !== option.key) : [...(value || []), option]);
      }

      setInput(option?.text || '');
      inputRef.current?.focus();
      setShowOptions(false);
      return onChange?.(option);
    },
    [input.length, isMultiple, onChange, suggestion, value],
  );

  const onEmptyOptionClick = useCallback(() => {
    setInput('');
    setShowOptions(false);
    inputRef.current?.focus();
    return onChange?.(undefined);
  }, [onChange]);

  const optionDeleteHandler = useCallback(
    (option: DropdownOption) => () => {
      if (!isMultiple || disabled) return;
      return onChange?.(value?.filter((x) => x.key !== option.key));
    },
    [isMultiple, onChange, value, disabled],
  );

  const inputClickHandler = useCallback(() => {
    setShowOptions((state) => !!suggestion || !state);
    setFilteredOptions(options);
  }, [options, suggestion]);

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        setShowOptions((state) => !state);
        setFilteredOptions(options);
      }
      if (event.code === 'ArrowDown') _.first(optionRefsArray)?.focus();
      if (event.code === 'ArrowUp') _.last(optionRefsArray.filter(Boolean))?.focus();
    },
    [optionRefsArray, options],
  );

  const optionKeyDownHandler = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    const active = document.activeElement;
    if (event.code === 'ArrowDown' && active?.nextElementSibling) {
      (active.nextElementSibling as HTMLElement).focus();
    }

    if (event.code === 'ArrowUp' && active?.previousElementSibling) {
      (active.previousElementSibling as HTMLElement).focus();
    }
  }, []);

  const inputChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInput(e.target.value);
      setFilteredOptions(
        e.target.value.length
          ? options.filter((x) => x.text.toLowerCase().includes(e.target.value.toLowerCase()))
          : options,
      );
    },
    [options],
  );

  const blurHandler = useCallback(() => {
    if (!isMultiple && suggestion && value?.text.length) {
      setInput(value?.text ?? '');
    }
  }, [isMultiple, suggestion, value]);

  const isOptionSelected = useCallback(
    (option: T) => (isMultiple ? value?.some((v) => v.key === option.key) : option.key === value?.key),
    [isMultiple, value],
  );

  return (
    <div className={className}>
      <div className={Style.inputWrapper}>
        <AppTextInput
          ref={(r) => (inputRef.current = r)}
          onChange={inputChangeHandler}
          value={input}
          onBlur={blurHandler}
          onClick={inputClickHandler}
          onKeyDown={keyDownHandler}
          readOnly={!suggestion}
          disabled={disabled}
          placeholder={placeholder}
          invalid={invalid}
          aria-owns={showOptions ? calloutId : undefined}
          inputClassName={clsx(Style.input, inputClassName)}
        />
        {CaretIcon ? (
          <CaretIcon data-rotate={showOptions} className={clsx(Style.chevron, caretClassName)} />
        ) : (
          !hideCaret && <Chevron data-rotate={showOptions} className={Style.chevron} />
        )}
      </div>
      {showOptions && (
        <Callout
          id={calloutId}
          target={inputRef}
          isBeakVisible={false}
          className={Style.callout}
          onRestoreFocus={() => {}}
          onDismiss={() => setShowOptions(false)}
          calloutMaxHeight={window.innerHeight * 0.6}
          directionalHint={DirectionalHint.bottomLeftEdge}
          styles={() => ({ calloutMain: { width: inputRef.current?.clientWidth } })}
        >
          <div className={Style.options}>
            {!isRequired && !isMultiple && (
              <button className={Style.emptyOption} onClick={onEmptyOptionClick} onKeyDown={optionKeyDownHandler}>
                <span className={'truncate'}>{emptyOptionLabel || t('Common_NotSelected')}</span>
              </button>
            )}
            {filteredOptions.map((option, i) => (
              <button
                ref={(ref) => (optionRefsArray[i] = ref)}
                key={option.key}
                className={Style.option}
                data-selected={isOptionSelected(option)}
                onClick={onOptionClick(option)}
                onKeyDown={optionKeyDownHandler}
              >
                <DropdownOptionComponent<T> item={option} selected={isOptionSelected(option)} />
              </button>
            ))}
          </div>
        </Callout>
      )}
      {isMultiple && value && value?.length > 0 && (
        <div className={Style.valuesContainer}>
          {value?.map((item) => (
            <div
              key={item.key}
              style={{ maxWidth: (inputRef.current?.clientWidth ?? 240) / 2 - 4 }}
              className={clsx(Style.valueWrapper, { [Style.isDisabled]: disabled })}
            >
              {item.icon}
              <p className={TypographyStyles.plainText12}>{item.text}</p>
              <Cross data-disabled={!!disabled} className={Style.deleteIcon} onClick={optionDeleteHandler(item)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
